import { useEffect, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { callBackbonePostApi } from '../services/backboneApiService';
import { BACKBONE_ACTIONS } from '../config/exercises';
import { getUser } from '../utils/cookie';

export const PERFORMANCE_ERROR = {
  SIGN_IN: 'sign_in',
  UNAVAILABLE: 'unavailable',
  VALIDATION: 'validation',
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict',
};

const CACHE_KEY = 'memberPerformanceProfile';
const SHARED_CACHE_KEY = 'sharedMemberPerformance';

// Only an expired or missing token (HTTP 401) means "sign in". Everything else is "unavailable".
const SIGN_IN_STATUS = 401;

// Non-empty identity for the signed-in user, or '' when there is none.
export const getPerformanceIdentity = (user) =>
  String(user?.userId || '').trim() ||
  String(user?.username || '')
    .trim()
    .toLowerCase();

// Drop every cached profile (all users, all windows) without triggering a refetch.
export const clearPerformanceCache = (mutate) =>
  mutate(
    (key) => Array.isArray(key) && (key[0] === CACHE_KEY || key[0] === SHARED_CACHE_KEY),
    undefined,
    { revalidate: false }
  );

export const normalizeUsername = (name) =>
  String(name || '')
    .trim()
    .toLowerCase();

const fetchProfile = async ([, identity, windowWeeks]) => {
  let response;
  try {
    response = await callBackbonePostApi({
      action: BACKBONE_ACTIONS.GET_MEMBER_PERFORMANCE_PROFILE,
      windowWeeks,
    });
  } catch (err) {
    // The shared axios interceptor rejects with `error.response`, so `status` is on `err`.
    const status = err?.status ?? err?.response?.status;
    const kind =
      status === SIGN_IN_STATUS ? PERFORMANCE_ERROR.SIGN_IN : PERFORMANCE_ERROR.UNAVAILABLE;
    throw Object.assign(new Error('Performance profile request failed'), { kind });
  }
  const body = response?.data;
  if (!body || body.result === false) {
    throw Object.assign(new Error('Performance profile unavailable'), {
      kind: PERFORMANCE_ERROR.UNAVAILABLE,
    });
  }
  // Tag the data with its owner so a profile never renders for a different user.
  return { identity, body };
};

// Only a plain string is ever shown to the member, and never an unbounded one.
const MAX_SERVER_MESSAGE = 200;
const safeMessage = (value) =>
  typeof value === 'string' ? value.trim().slice(0, MAX_SERVER_MESSAGE) : '';

const CONFLICT_MESSAGE = 'Your targets changed elsewhere. Reload and try again.';

const writeError = (kind, userMessage = '') =>
  Object.assign(new Error(userMessage || 'Performance write failed'), { kind, userMessage });

// Self-only writes (away periods, targets, sharing): the server derives the member from the
// token, so the body never carries a user id. 400 carries a user-facing `errorMessage`; 401
// means sign in; 404 means the item no longer exists; 409 means the save conflicted with a
// change made elsewhere (the server's text is not shown, ours is).
const callWriteAction = async (action, body) => {
  let response;
  try {
    response = await callBackbonePostApi({ action, ...body });
  } catch (err) {
    const status = err?.status ?? err?.response?.status;
    const data = err?.data ?? err?.response?.data;
    if (status === SIGN_IN_STATUS) {
      throw writeError(PERFORMANCE_ERROR.SIGN_IN);
    }
    if (status === 404) {
      throw writeError(PERFORMANCE_ERROR.NOT_FOUND);
    }
    if (status === 409) {
      throw writeError(PERFORMANCE_ERROR.CONFLICT, CONFLICT_MESSAGE);
    }
    const message = status === 400 ? safeMessage(data?.errorMessage) : '';
    throw writeError(
      message ? PERFORMANCE_ERROR.VALIDATION : PERFORMANCE_ERROR.UNAVAILABLE,
      message
    );
  }
  const data = response?.data;
  if (!data || data.result === false) {
    const message = safeMessage(data?.errorMessage);
    throw writeError(
      message ? PERFORMANCE_ERROR.VALIDATION : PERFORMANCE_ERROR.UNAVAILABLE,
      message
    );
  }
  return data;
};

// The signed-in viewer's own profile; `enabled: false` makes it a no-op (shared view).
const useMemberPerformanceProfile = (windowWeeks, { enabled = true } = {}) => {
  const { mutate: globalMutate } = useSWRConfig();
  const identity = getPerformanceIdentity(getUser());

  const { data, error, isLoading, mutate } = useSWR(
    identity && enabled ? [CACHE_KEY, identity, windowWeeks] : null,
    fetchProfile,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      keepPreviousData: true,
    }
  );

  // The swr cache is global across SPA login and logout, so drop it when the user changes.
  const previousIdentity = useRef(identity);
  useEffect(() => {
    if (previousIdentity.current !== identity) {
      previousIdentity.current = identity;
      clearPerformanceCache(globalMutate);
    }
  }, [identity, globalMutate]);

  const ownsData = data && data.identity === identity;
  let errorKind = null;
  if (!identity) {
    errorKind = PERFORMANCE_ERROR.SIGN_IN;
  } else if (enabled && error) {
    errorKind = error.kind || PERFORMANCE_ERROR.UNAVAILABLE;
  }

  // The window can change while a write is in flight, so always refresh the one shown now.
  const windowRef = useRef(windowWeeks);
  windowRef.current = windowWeeks;

  // After a write every cached window for this member is stale. Drop the other windows (they
  // refetch when chosen) and refetch the one on screen, keeping its data visible meanwhile.
  const refreshAfterWrite = async () => {
    await globalMutate(
      (key) =>
        Array.isArray(key) &&
        key[0] === CACHE_KEY &&
        key[1] === identity &&
        key[2] !== windowRef.current,
      undefined,
      // Revalidating an unmounted key only drops its in-flight request record, so a window
      // chosen within the dedupe interval cannot reuse the pre-write request.
      { revalidate: true }
    );
    try {
      await globalMutate([CACHE_KEY, identity, windowRef.current]);
    } catch (err) {
      // The write succeeded; a failed refresh is surfaced by the page's own refresh error.
    }
  };

  const runAwayAction = async (action, body, { refreshOnError = false } = {}) => {
    try {
      await callWriteAction(action, body);
    } catch (err) {
      if (refreshOnError && err.kind !== PERFORMANCE_ERROR.SIGN_IN) {
        await refreshAfterWrite();
      }
      throw err;
    }
    await refreshAfterWrite();
  };
  const addAwayPeriod = ({ startDate, endDate, label }) =>
    runAwayAction(BACKBONE_ACTIONS.ADD_MEMBER_AWAY_PERIOD, { startDate, endDate, label });
  const deleteAwayPeriod = (periodId) =>
    runAwayAction(
      BACKBONE_ACTIONS.DELETE_MEMBER_AWAY_PERIOD,
      { periodId },
      // A period that no longer exists (or a failed delete) means our list may be stale.
      { refreshOnError: true }
    );

  // Targets and sharing writes. The member comes from the token, never the body.
  const runWrite = async (action, body) => {
    const data = await callWriteAction(action, body);
    await refreshAfterWrite();
    return data;
  };
  const saveTargets = (targets) =>
    runWrite(BACKBONE_ACTIONS.SET_MEMBER_PERFORMANCE_TARGETS, targets);
  const resetTargets = () =>
    runWrite(BACKBONE_ACTIONS.SET_MEMBER_PERFORMANCE_TARGETS, { reset: true });
  const setSharing = (isEnabled) =>
    runWrite(BACKBONE_ACTIONS.SET_MEMBER_PERFORMANCE_SHARING, { enabled: isEnabled });

  return {
    addAwayPeriod,
    deleteAwayPeriod,
    saveTargets,
    resetTargets,
    setSharing,
    profile: enabled && ownsData ? data.body : null,
    isLoading: enabled && Boolean(identity) && isLoading,
    errorKind,
    refetch: mutate,
  };
};

// Defence in depth: the shared view never holds private fields, even if a server sends them:
// the private cards, `consistency.awayWeeks` and `consistency.lastSessionAt`, and away/break
// week statuses (shown as idle).
const PRIVATE_SHARED_FIELDS = [
  'awayPeriods',
  'targets',
  'targetProgress',
  'nextSession',
  'sharing',
];
export const sanitizeSharedBody = (body) => {
  const clean = { ...body };
  PRIVATE_SHARED_FIELDS.forEach((field) => {
    delete clean[field];
  });
  if (clean.consistency && typeof clean.consistency === 'object') {
    const { awayWeeks, lastSessionAt, ...consistency } = clean.consistency;
    clean.consistency = consistency;
  }
  if (Array.isArray(clean.weeks)) {
    clean.weeks = clean.weeks.map((w) =>
      w && (w.status === 'away' || w.status === 'break') ? { ...w, status: 'idle' } : w
    );
  }
  return clean;
};

const fetchSharedProfile = async ([, identity, username, windowWeeks]) => {
  let response;
  try {
    response = await callBackbonePostApi({
      action: BACKBONE_ACTIONS.GET_SHARED_MEMBER_PERFORMANCE,
      username,
      windowWeeks,
    });
  } catch (err) {
    const status = err?.status ?? err?.response?.status;
    let kind = PERFORMANCE_ERROR.UNAVAILABLE;
    if (status === SIGN_IN_STATUS) {
      kind = PERFORMANCE_ERROR.SIGN_IN;
    } else if (status === 404) {
      // Not shared and no such member look the same on purpose.
      kind = PERFORMANCE_ERROR.NOT_FOUND;
    }
    throw Object.assign(new Error('Shared performance request failed'), { kind });
  }
  const body = response?.data;
  if (!body || body.result === false) {
    throw Object.assign(new Error('Shared performance unavailable'), {
      kind: PERFORMANCE_ERROR.UNAVAILABLE,
    });
  }
  // Tag with the viewer AND the member so one member's data never renders for another.
  return { identity, username, body: sanitizeSharedBody(body) };
};

// Another member's shared, read-only profile. Pass a falsy username to stay idle.
export const useSharedMemberPerformance = (username, windowWeeks = 26) => {
  const { mutate: globalMutate } = useSWRConfig();
  const identity = getPerformanceIdentity(getUser());
  const target = normalizeUsername(username);
  const enabled = Boolean(target);

  const { data, error, isLoading, mutate } = useSWR(
    identity && enabled ? [SHARED_CACHE_KEY, identity, target, windowWeeks] : null,
    fetchSharedProfile,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      keepPreviousData: true,
    }
  );

  const previousIdentity = useRef(identity);
  useEffect(() => {
    if (previousIdentity.current !== identity) {
      previousIdentity.current = identity;
      clearPerformanceCache(globalMutate);
    }
  }, [identity, globalMutate]);

  const ownsData = data && data.identity === identity && data.username === target;
  let errorKind = null;
  if (enabled && !identity) {
    errorKind = PERFORMANCE_ERROR.SIGN_IN;
  } else if (enabled && error) {
    errorKind = error.kind || PERFORMANCE_ERROR.UNAVAILABLE;
  }

  return {
    profile: enabled && ownsData ? data.body : null,
    isLoading: enabled && Boolean(identity) && isLoading,
    errorKind,
    refetch: mutate,
  };
};

export default useMemberPerformanceProfile;
